import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from 'flarum/forum/app';

/**
 * A Pusher-shaped client that never opens a socket.
 *
 * flarum/realtime wires everything — channel subscriptions, event bindings,
 * liveness tracking, reconnect catch-up — through the object it assigns to
 * `app.websocket`, using a small slice of the pusher-js surface. This
 * implements exactly that slice over HTTP polling: one GET collects events
 * for every subscribed channel by cursor, client events (typing) become one
 * POST, and realtime's own code runs unmodified on top.
 *
 * Cadence: the server suggests the interval (default 3s); a hidden tab stops
 * polling entirely and fires one immediate poll when it returns; a tab idle
 * for a while stretches the interval; every wait is jittered so a forum's
 * tabs don't synchronize into request spikes.
 */

const IDLE_AFTER_MS = 5 * 60 * 1000;
const IDLE_MULTIPLIER = 5;
const JITTER = 0.2;
/**
 * pusher-js's unbind rules: by event, by callback, by both, or everything
 * when given neither.
 */
function keep(b, event, callback) {
  return !!event && b.event !== event || !!callback && b.callback !== callback;
}

/**
 * One subscribed channel, with the pusher-js Channel methods extensions use.
 * realtime itself needs only bind/unbind/trigger, but flarum/messages calls
 * unsubscribe() and Flarum Deck bind_global(), and a missing method throws
 * in their code, not ours; so the whole public surface is here.
 */
class PollingChannel {
  constructor(name, socket) {
    this.name = name;
    this.socket = socket;
    _defineProperty(this, "bindings", []);
    /** pusher-js: handlers for every event on this channel, called with (event, data). */
    _defineProperty(this, "globals", []);
    /** pusher-js: true once subscribed; a poll subscribes on the next request. */
    _defineProperty(this, "subscribed", true);
    _defineProperty(this, "subscriptionPending", false);
    _defineProperty(this, "subscriptionCancelled", false);
  }
  bind(event, callback) {
    this.bindings.push({
      event,
      callback
    });
    return this;
  }
  unbind(event, callback) {
    this.bindings = this.bindings.filter(b => keep(b, event, callback));
    return this;
  }

  /**
   * pusher-js: every event on the channel, whatever its name. Extensions that
   * want all of realtime's traffic bind this way (Flarum Deck does, on the
   * public and user channels), so a shim without it throws as they start.
   */
  bind_global(callback) {
    this.globals.push(callback);
    return this;
  }
  unbind_global(callback) {
    this.globals = callback ? this.globals.filter(g => g !== callback) : [];
    return this;
  }
  unbind_all() {
    this.bindings = [];
    this.globals = [];
    return this;
  }

  /** pusher-js: leave the channel. flarum/messages does this on leaving a conversation. */
  unsubscribe() {
    this.subscribed = false;
    this.socket.unsubscribe(this.name);
  }

  /** pusher-js: (re)join; polling picks the channel up on its next request. */
  subscribe() {
    this.socket.subscribe(this.name);
  }

  /** A client event: POSTed, fire and forget. Pusher returns a boolean. */
  trigger(event, data) {
    this.socket.post(this.name, event, data);
    return true;
  }
  dispatch(event, data) {
    // One broken handler must not stop the rest.
    this.bindings.forEach(b => {
      if (b.event === event) {
        try {
          b.callback(data);
        } catch (_unused) {}
      }
    });
    this.globals.forEach(g => {
      try {
        g(event, data);
      } catch (_unused2) {}
    });
    this.socket.dispatchGlobal(this.name, event, data);
  }
}
export default class PollingSocket {
  constructor() {
    /** Mirrors pusher-js's registry; realtime never reads it, but other
     *  extensions may, and keeping the same shape costs nothing. */
    _defineProperty(this, "channels", {
      channels: {}
    });
    _defineProperty(this, "connection", {
      state: 'initialized',
      bindings: [],
      bind: (event, callback) => {
        this.connection.bindings.push({
          event,
          callback
        });
        return this.connection;
      },
      unbind: (event, callback) => {
        this.connection.bindings = this.connection.bindings.filter(b => keep(b, event, callback));
        return this.connection;
      }
    });
    /** pusher-js: socket-wide handlers, for one event on any channel. */
    _defineProperty(this, "bindings", []);
    _defineProperty(this, "globals", []);
    _defineProperty(this, "cursor", null);
    _defineProperty(this, "interval", 3000);
    _defineProperty(this, "timer", null);
    _defineProperty(this, "stopped", false);
    _defineProperty(this, "polling", false);
    _defineProperty(this, "lastActivity", Date.now());
    /** This tab's identity, so its own client events are not echoed back. */
    _defineProperty(this, "origin", Math.random().toString(36).slice(2, 14) + Math.random().toString(36).slice(2, 14));
    _defineProperty(this, "onVisibility", () => {
      if (document.visibilityState === 'visible' && !this.stopped) {
        this.schedule(0);
      }
    });
    _defineProperty(this, "onActivity", () => {
      this.lastActivity = Date.now();
    });
    this.listen();
    this.schedule(0);
  }
  listen() {
    document.addEventListener('visibilitychange', this.onVisibility);
    ['pointerdown', 'keydown', 'scroll'].forEach(ev => document.addEventListener(ev, this.onActivity, {
      passive: true
    }));
  }

  /**
   * Bring a disconnected shim back to life.
   *
   * realtime rebuilds its client whenever it decides the connection is stale
   * (every iOS app-switch, and on any browser once a tab has been hidden past
   * its 65s liveness window). That path calls disconnect() on whatever is in
   * `app.websocket` first, which used to stop this loop for good: polling
   * never resumed until the page was reloaded. The cursor is deliberately
   * kept, so the catch-up poll picks up exactly where the old one left off.
   */
  restart() {
    if (!this.stopped) return;
    this.stopped = false;
    this.listen();
    this.setState('initialized');
    this.schedule(0);
  }

  /** pusher-js: called on every incoming frame; realtime feeds liveness from it. */
  bind_global(callback) {
    this.globals.push(callback);
    return this;
  }
  unbind_global(callback) {
    this.globals = callback ? this.globals.filter(g => g !== callback) : [];
    return this;
  }

  /** pusher-js: one event, whichever channel it arrives on. */
  bind(event, callback) {
    this.bindings.push({
      event,
      callback
    });
    return this;
  }
  unbind(event, callback) {
    this.bindings = this.bindings.filter(b => keep(b, event, callback));
    return this;
  }
  unbind_all() {
    this.bindings = [];
    this.globals = [];
    return this;
  }

  /** pusher-js: a subscribed channel by name. */
  channel(name) {
    return this.channels.channels[name];
  }
  allChannels() {
    return Object.values(this.channels.channels);
  }

  /** pusher-js: reconnect after disconnect(). */
  connect() {
    this.restart();
  }

  /** An event on one channel, handed on to the socket-wide handlers. */
  dispatchGlobal(channel, event, data) {
    this.bindings.forEach(b => {
      if (b.event === event) {
        try {
          b.callback(data);
        } catch (_unused3) {}
      }
    });
    this.globals.forEach(g => {
      try {
        g(event, data);
      } catch (_unused4) {}
    });
  }
  subscribe(name) {
    const existing = this.channels.channels[name];
    if (existing) return existing;
    const channel = new PollingChannel(name, this);
    this.channels.channels[name] = channel;
    return channel;
  }
  unsubscribe(name) {
    const channel = this.channels.channels[name];
    if (channel) channel.subscribed = false;
    delete this.channels.channels[name];
  }
  disconnect() {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    document.removeEventListener('visibilitychange', this.onVisibility);
    ['pointerdown', 'keydown', 'scroll'].forEach(ev => document.removeEventListener(ev, this.onActivity));
    this.setState('disconnected');
  }
  post(channel, event, data) {
    app.request({
      method: 'POST',
      url: "".concat(app.forum.attribute('apiUrl'), "/warble/event"),
      body: {
        channel,
        event,
        data,
        origin: this.origin
      },
      errorHandler: () => {}
    }).catch(() => {});
  }
  schedule(delay) {
    if (this.stopped) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.poll(), delay);
  }
  nextDelay() {
    const idle = Date.now() - this.lastActivity > IDLE_AFTER_MS;
    const base = this.interval * (idle ? IDLE_MULTIPLIER : 1);
    return base * (1 - JITTER + Math.random() * JITTER * 2);
  }
  async poll() {
    if (this.stopped || this.polling) return;

    // A hidden tab does not poll; visibilitychange restarts it.
    if (document.visibilityState === 'hidden') return;
    this.polling = true;
    const names = Object.keys(this.channels.channels);
    try {
      const params = new URLSearchParams({
        channels: names.join(','),
        origin: this.origin
      });
      if (this.cursor !== null) params.set('cursor', String(this.cursor));
      const data = await app.request({
        method: 'GET',
        url: "".concat(app.forum.attribute('apiUrl'), "/warble/poll?").concat(params),
        errorHandler: () => {}
      });
      this.cursor = data.cursor;
      this.interval = Math.max(2000, (data.interval || 3) * 1000);

      // Every successful poll is a frame: feeds realtime's liveness clock.
      // Named like the keep-alive a socket sends, so a handler that reads the
      // event name gets one, and can skip it as it would on a socket.
      this.globals.forEach(g => {
        try {
          g('pusher:pong', {});
        } catch (_unused5) {}
      });
      this.setState('connected');
      (data.events || []).forEach(e => {
        var _this$channels$channe;
        (_this$channels$channe = this.channels.channels[e.channel]) == null || _this$channels$channe.dispatch(e.event, e.data);
      });
    } catch (_unused6) {
      // The next poll retries; realtime's own stale-connection handling sees
      // the liveness clock stop and recovers with a catch-up on reconnect.
      this.setState('unavailable');
    } finally {
      this.polling = false;
      this.schedule(this.nextDelay());
    }
  }
  setState(state) {
    if (this.connection.state === state) return;
    const previous = this.connection.state;
    this.connection.state = state;
    this.connection.bindings.forEach(b => {
      if (b.event === 'state_change') {
        try {
          b.callback({
            previous,
            current: state
          });
        } catch (_unused7) {}
      }
    });
  }
}